import { useMemo } from 'react';

import { Spinner } from '@kc-console/common-ui';
import { env, getUserGuidePath } from '@kc-console/context';
import { STATUS_CODE, t } from '@kc-console/core';
import IllustStatusForbidden from '@kep/kcds-icons/illust/IllustStatusForbidden';

import { EMPTY_STATE } from '../EmptyState/types';
import { GuideDocsOutLink } from '../GuideDocsOutLink';
import useDataTableStatus from './useDataTableStatus';

import type { StatusCode } from '@kc-console/core';
import type { EmptyStateProps, EmptyStateType } from '../EmptyState';
import type { DataTableEmptyStateProps } from './DataTableEmptyState';
import type { DataTableErrorState } from './types';

type DataTableEmptyStateConfig = Partial<
  Record<EmptyStateType, Omit<EmptyStateProps, 'type' | 'onRefetch'>>
>;
type DataTableEmptyStateItem = Omit<EmptyStateProps, 'type' | 'onRefetch'>;
type DataTableErrorEmptyState = NonNullable<DataTableErrorState['emptyState']>;

interface UseDataTableEmptyStateProps {
  hasFilters: boolean;
  rowsLength: number;
  showSideBorder: boolean;
  emptyState?: DataTableEmptyStateConfig;
  emptyStateProps?: Pick<DataTableEmptyStateProps, 'containerStyle'>;
  error?: unknown;
  errorState?: DataTableErrorState;
  isError?: boolean;
  isLoading?: boolean;
  layoutType?: 'detail' | 'list';
}

const EMPTY_ILLUSTRATION = null;
const DETAIL_LOADING_ILLUSTRATION = <Spinner size="md" />;

const getListDefaultErrorState = (): DataTableErrorState => ({
  ignoredCodes: [],
  emptyState: {
    [STATUS_CODE.FORBIDDEN]: {
      illustration: <IllustStatusForbidden />,
      message: t('ui:empty_state.forbidden.message'),
      guideText: t('ui:empty_state.forbidden.guide_text'),
      action: <GuideDocsOutLink href={getUserGuidePath(env.KC_FORBIDDEN_GUIDE_LINK)} />,
    },
  },
});

const getDetailDefaultErrorState = (): DataTableErrorState => ({
  ignoredCodes: [],
  emptyState: {
    [STATUS_CODE.FORBIDDEN]: {
      illustration: null,
      message: t('ui:empty_state.forbidden_simple.message'),
      guideText: null,
      action: <GuideDocsOutLink href={getUserGuidePath(env.KC_FORBIDDEN_GUIDE_LINK)} />,
    },
  },
});

const getDefaultErrorState = (layoutType: 'detail' | 'list'): DataTableErrorState => {
  return layoutType === 'detail' ? getDetailDefaultErrorState() : getListDefaultErrorState();
};

const mergeErrorEmptyState = (
  defaultEmptyState: DataTableErrorEmptyState,
  customEmptyState?: DataTableErrorState['emptyState'],
): DataTableErrorEmptyState => {
  if (!customEmptyState) return defaultEmptyState;

  return (Object.values(STATUS_CODE) as StatusCode[]).reduce<DataTableErrorEmptyState>(
    (result, statusCode) => {
      const customStatusEmptyState = customEmptyState[statusCode];
      if (!customStatusEmptyState) return result;

      return {
        ...result,
        [statusCode]: {
          ...defaultEmptyState[statusCode],
          ...customStatusEmptyState,
        },
      };
    },
    { ...defaultEmptyState },
  );
};

const isIgnoredErrorStatus = (params: {
  errorStatus: StatusCode | null;
  ignoredCodes: StatusCode[];
}) => {
  const { errorStatus, ignoredCodes } = params;

  return !!errorStatus && ignoredCodes.includes(errorStatus);
};

export default function useDataTableEmptyState({
  emptyState,
  emptyStateProps,
  error,
  errorState,
  hasFilters,
  isError,
  isLoading,
  layoutType,
  rowsLength,
  showSideBorder,
}: UseDataTableEmptyStateProps) {
  // layoutType이 명시되지 않은 기존 사용처는 showSideBorder로 list/detail 레이아웃을 구분합니다.
  const refinedLayoutType = layoutType || (showSideBorder ? 'detail' : 'list');
  const listEmptyState = emptyState?.list;
  const searchEmptyState = emptyState?.search;
  const warningEmptyState = emptyState?.warning;
  const loadingEmptyState = emptyState?.loading;

  const resolvedErrorState = useMemo(() => {
    const defaultErrorState = getDefaultErrorState(refinedLayoutType);

    // 기본 forbidden 정책 위에 서비스별 ignoredCodes/status code emptyState 정책을 얹습니다.
    return {
      ignoredCodes: errorState?.ignoredCodes ?? defaultErrorState.ignoredCodes,
      emptyState: mergeErrorEmptyState(defaultErrorState.emptyState ?? {}, errorState?.emptyState),
    };
  }, [errorState?.emptyState, errorState?.ignoredCodes, refinedLayoutType]);

  const { dataEmptyStateType, emptyStateType, errorStatus } = useDataTableStatus({
    error,
    hasFilters,
    isError,
    isLoading,
    rowsLength,
  });

  const resolvedEmptyStateType = useMemo(() => {
    if (emptyStateType !== EMPTY_STATE.WARNING) return emptyStateType;

    // ignoredCodes에 포함된 에러는 warning 대신 기존 데이터 empty state(list/search/null)를 사용합니다.
    if (
      isIgnoredErrorStatus({
        errorStatus,
        ignoredCodes: resolvedErrorState.ignoredCodes ?? [],
      })
    ) {
      return dataEmptyStateType;
    }

    return emptyStateType;
  }, [dataEmptyStateType, emptyStateType, errorStatus, resolvedErrorState.ignoredCodes]);

  const refinedEmptyState = useMemo(() => {
    const isListLayout = refinedLayoutType === 'list';

    // detail 레이아웃에서는 좁은 테이블 높이에 맞춰 illustration을 숨기고, loading만 spinner로 대체합니다.
    const createEmptyState = (
      config: DataTableEmptyStateItem | undefined,
      detailIllustration: EmptyStateProps['illustration'] = EMPTY_ILLUSTRATION,
    ) => ({
      ...config,
      illustration: isListLayout ? config?.illustration : detailIllustration,
    });

    return {
      list: createEmptyState(listEmptyState),
      loading: createEmptyState(loadingEmptyState, DETAIL_LOADING_ILLUSTRATION),
      search: createEmptyState(searchEmptyState),
      warning: createEmptyState(warningEmptyState),
    };
  }, [refinedLayoutType, listEmptyState, searchEmptyState, warningEmptyState, loadingEmptyState]);

  const resolvedEmptyState = useMemo(() => {
    if (!resolvedEmptyStateType || !errorStatus) return refinedEmptyState;

    if (resolvedEmptyStateType !== EMPTY_STATE.WARNING) return refinedEmptyState;

    const errorEmptyState = resolvedErrorState.emptyState[errorStatus];
    if (!errorEmptyState) return refinedEmptyState;

    // HTTP 상태 코드별 설정은 공통 error emptyState보다 우선 적용합니다.
    return {
      ...refinedEmptyState,
      [resolvedEmptyStateType]: {
        ...refinedEmptyState[resolvedEmptyStateType],
        ...errorEmptyState,
      },
    };
  }, [resolvedEmptyStateType, errorStatus, resolvedErrorState.emptyState, refinedEmptyState]);

  const refinedEmptyStateProps = useMemo(() => {
    if (emptyStateProps) return emptyStateProps;
    if (refinedLayoutType === 'list') return undefined;

    // detail 레이아웃은 illustration을 줄인 대신 empty state 영역이 과하게 커지지 않도록 높이를 낮춥니다.
    return {
      containerStyle: {
        minHeight: '90px',
      },
    };
  }, [emptyStateProps, refinedLayoutType]);

  return {
    emptyState: resolvedEmptyState,
    emptyStateProps: refinedEmptyStateProps,
    emptyStateType: resolvedEmptyStateType,
  };
}

export { getDetailDefaultErrorState, getListDefaultErrorState };
